import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  BarChart3,
  Camera,
  ChartPie,
  ClipboardList,
  IceCreamBowl,
  ImagePlus,
  Images,
  Inbox,
  Loader2,
  LogOut,
  Mail,
  Pizza,
  Plus,
  RefreshCw,
  Save,
  Send,
  Trash2,
  Truck,
  Utensils,
  Wine
} from "lucide-react";
import { DEFAULT_CATERING_ALGORITHM, normalizeCateringAlgorithm } from "./catering.js";

const EMPTY_ITEM = {
  id: "",
  canonicalItemId: "",
  name: "",
  description: "",
  price: "",
  imageUrl: "",
  imageAlt: "",
  imagePosition: "center",
  tags: [],
  isFeatured: false,
  isSecret: false,
  isOutOfSeason: false,
  isActive: true
};

const EMPTY_SECTION = {
  id: "",
  title: "",
  slug: "",
  note: "",
  statusLabel: "",
  isStatusActive: false,
  isActive: true,
  items: []
};

const EMPTY_FLAVOR = {
  id: "",
  name: "",
  description: "",
  imageUrl: "",
  imagePosition: "center",
  tags: [],
  isFeatured: false,
  isOutOfSeason: false,
  isActive: true
};

const EMPTY_FORM = {
  id: "catering_quote",
  name: "Catering quote request",
  description: "",
  recipientEmails: [],
  subjectTemplate: "New Gelato Spot catering request from {name}",
  isActive: true
};

const MANNA_LOGO_SRC = "/api/assets/optimized/manna-admin-logo.webp";
const ADMIN_IMAGE_UPLOAD_MAX_BYTES = 30 * 1024 * 1024;
const ADMIN_LOGOUT_EVENT = "gelato-admin-logout";
const ADMIN_TABS = [
  { id: "flavors", label: "Flavors", Icon: IceCreamBowl },
  { id: "menu", label: "Menu items", Icon: Pizza },
  { id: "ranking", label: "Ranking", Icon: BarChart3 },
  { id: "subscribers", label: "Subscribers", Icon: Mail },
  { id: "forms", label: "Forms", Icon: ClipboardList },
  { id: "catering", label: "Catering", Icon: Truck }
];
const RANKING_CATEGORIES = [
  { id: "gelato", label: "Gelato", Icon: IceCreamBowl },
  { id: "dining", label: "Dining", Icon: Utensils },
  { id: "alcohol", label: "Alcohol", Icon: Wine }
];
const RANKING_PERIODS = [
  ["this_week", "This week", "Mon-Sun"],
  ["last_week", "Last week", "Mon-Sun"],
  ["this_month", "This month", ""],
  ["last_month", "Last month", ""],
  ["this_year", "This year", ""],
  ["last_year", "Last year", ""],
  ["all_time", "All time", ""]
];
const RANKING_COLORS = ["#c59d5f", "#263b36", "#d36955", "#7e9b85", "#d1b16d", "#9f664d", "#879eb1", "#62765d"];
const RANKING_VARIANT_COLORS = {
  Dinner: "#c59d5f",
  Lunch: "#263b36",
  Standard: "#a8a39a"
};
const IMAGE_POSITION_OPTIONS = [
  ["center", "Center"],
  ["top", "Top"],
  ["bottom", "Bottom"],
  ["left", "Left"],
  ["right", "Right"],
  ["top left", "Top left"],
  ["top right", "Top right"],
  ["bottom left", "Bottom left"],
  ["bottom right", "Bottom right"]
];
const IMAGE_POSITION_VALUES = new Set(IMAGE_POSITION_OPTIONS.map(([value]) => value));

const CATERING_FIELDS = [
  ["basePrice", "Base price"],
  ["includedPeople", "Included people"],
  ["pricePerExtraGuest", "Extra guest price"],
  ["includedHours", "Included hours"],
  ["pricePerExtraHour", "Extra hour price"],
  ["staffFee", "Staff fee"],
  ["includedFlavors", "Included flavors"],
  ["pricePerExtraFlavor", "Extra flavor price"],
  ["minPeople", "Minimum people"],
  ["maxPeople", "Maximum people"],
  ["minHours", "Minimum hours"],
  ["maxHours", "Maximum hours"],
  ["minFlavors", "Minimum flavors"],
  ["maxFlavors", "Maximum flavors"]
];

function rowId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function tagsText(tags) {
  return Array.isArray(tags) ? tags.join(", ") : String(tags || "");
}

function emailsText(emails) {
  return Array.isArray(emails) ? emails.join(", ") : String(emails || "");
}

function isPersistedId(value) {
  return /^\d+$/.test(String(value || ""));
}

function formatDateTime(value) {
  if (!value) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function formatFileSize(bytes) {
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

function imagePositionValue(value) {
  const position = String(value || "").trim().toLowerCase();
  return IMAGE_POSITION_VALUES.has(position) ? position : "center";
}

function shouldShowImageSourceChoice() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }

  return window.matchMedia("(max-width: 820px)").matches || window.matchMedia("(pointer: coarse)").matches;
}

function isCoffeeSection(section) {
  const slug = String(section?.slug || section?.title || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return slug === "coffee";
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options
  });
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(payload.message || "Request failed.");
    error.status = response.status;
    throw error;
  }

  return payload;
}

async function uploadAdminImageFile(file, { kind, label }) {
  const params = new URLSearchParams({
    kind,
    name: label || file.name || "image"
  });
  const response = await fetch(`/api/admin/assets?${params.toString()}`, {
    method: "POST",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      "X-File-Name": file.name || label || "image"
    },
    body: file
  });
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(payload.message || "Image upload failed.");
    error.status = response.status;
    throw error;
  }

  return payload.asset;
}

function normalizeAdminData(payload) {
  return {
    menu: {
      sections: payload.menu?.sections || []
    },
    flavors: payload.flavors?.flavors || [],
    catering: normalizeCateringAlgorithm(payload.catering?.algorithm || DEFAULT_CATERING_ALGORITHM),
    forms: payload.forms?.forms || [],
    submissions: payload.submissions?.submissions || [],
    subscribers: payload.subscribers || { count: 0, subscribers: [] },
    sms: payload.sms || { provider: "twilio", configured: false, enabled: false, missing: [] },
    marketingEmail: payload.marketingEmail || {
      provider: "resend",
      syncConfigured: false,
      syncEnabled: false,
      sendingEnabled: false,
      broadcastsUrl: "https://resend.com/broadcasts"
    },
    email: payload.email || { provider: "resend", configured: false, from: "" },
    storage: payload.storage || { configured: false, uploadMode: "unavailable", missing: [] }
  };
}

function StatusLine({ status }) {
  if (!status.message) {
    return null;
  }

  return <p className={`form-status ${status.state}`}>{status.message}</p>;
}

function StorageNotice({ storage }) {
  if (storage?.configured) {
    return null;
  }

  const missing = (storage?.missing || [])
    .map((item) => item.field)
    .filter(Boolean)
    .join(", ");

  return (
    <div className="admin-config-notice" role="status">
      <strong>Image uploads need bucket storage.</strong>
      <span>
        {missing
          ? `Missing ${missing}. Add bucket variables to local .env or pull the Railway variables.`
          : "Add the bucket endpoint, bucket name, access key, and secret key."}
      </span>
    </div>
  );
}

function AdminImageSourceSheet({ label, onCamera, onLibrary, onClose }) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="admin-image-source-backdrop" role="presentation" onPointerDown={onClose}>
      <div
        className="admin-image-source-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`Photo source${label ? ` for ${label}` : ""}`}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="admin-image-source-head">
          <p className="eyebrow">Photo</p>
          <h3>{label ? label : "Add image"}</h3>
        </div>
        <div className="admin-image-source-actions">
          <button type="button" onClick={onCamera}>
            <Camera size={20} aria-hidden="true" />
            <span>Take photo</span>
          </button>
          <button type="button" onClick={onLibrary}>
            <Images size={20} aria-hidden="true" />
            <span>Choose photo</span>
          </button>
        </div>
        <button type="button" className="admin-image-source-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>,
    document.body
  );
}

function AdminImageUpload({
  imageUrl,
  imageAlt = "",
  imagePosition = "center",
  label,
  kind,
  isOutOfSeason = false,
  onUploaded,
  setStatus
}) {
  const libraryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const hasImage = Boolean(imageUrl) && !imageFailed;
  const previewPosition = imagePositionValue(imagePosition);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  useEffect(() => {
    if (uploading) {
      setSourcePickerOpen(false);
    }
  }, [uploading]);

  const chooseFile = () => {
    if (uploading) {
      return;
    }

    if (shouldShowImageSourceChoice()) {
      setSourcePickerOpen(true);
      return;
    }

    libraryInputRef.current?.click();
  };

  const chooseSource = (inputRef) => {
    setSourcePickerOpen(false);
    inputRef.current?.click();
  };

  const handleChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setStatus({ state: "error", message: "Choose an image file." });
      return;
    }

    if (file.size > ADMIN_IMAGE_UPLOAD_MAX_BYTES) {
      setStatus({
        state: "error",
        message: `Choose an image under ${formatFileSize(ADMIN_IMAGE_UPLOAD_MAX_BYTES)}. We convert uploads to optimized WebP files.`
      });
      return;
    }

    setUploading(true);
    setStatus({ state: "loading", message: "Uploading and optimizing photo..." });

    try {
      const asset = await uploadAdminImageFile(file, { kind, label });
      onUploaded(asset.url);
      setStatus({ state: "success", message: "Photo converted to WebP. Save this section to publish the new image." });
    } catch (error) {
      setStatus({ state: "error", message: error.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="admin-image-field">
      <button
        type="button"
        className={`admin-image-upload ${hasImage ? "has-image" : "is-empty"}${isOutOfSeason ? " is-out-of-season" : ""}`}
        onClick={chooseFile}
        disabled={uploading}
        aria-label={`${hasImage ? "Replace" : "Upload"} photo${label ? ` for ${label}` : ""}`}
      >
        {hasImage ? (
          <img
            src={imageUrl}
            alt={imageAlt || ""}
            style={{ objectPosition: previewPosition }}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <ImagePlus size={28} />
        )}
        {uploading ? (
          <span className="admin-image-upload-state">
            <Loader2 size={22} aria-hidden="true" />
          </span>
        ) : hasImage ? (
          <span className="admin-image-upload-cue">
            <ImagePlus size={16} aria-hidden="true" />
          </span>
        ) : null}
        {isOutOfSeason ? <span className="admin-image-season-badge">Out</span> : null}
      </button>
      <input ref={libraryInputRef} className="admin-file-input" type="file" accept="image/*" onChange={handleChange} tabIndex={-1} />
      <input
        ref={cameraInputRef}
        className="admin-file-input"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleChange}
        tabIndex={-1}
      />
      {sourcePickerOpen ? (
        <AdminImageSourceSheet
          label={label}
          onCamera={() => chooseSource(cameraInputRef)}
          onLibrary={() => chooseSource(libraryInputRef)}
          onClose={() => setSourcePickerOpen(false)}
        />
      ) : null}
    </div>
  );
}

function AdminLogin({ onLogin, status, setStatus }) {
  const [form, setForm] = useState({ email: "", password: "" });

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setStatus({ state: "loading", message: "Signing in..." });

    try {
      await requestJson("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(form)
      });
      await onLogin();
    } catch (error) {
      setStatus({ state: "error", message: error.message });
    }
  };

  return (
    <section className="admin-page admin-login-page">
      <div className="admin-login-card">
        <div className="admin-login-copy">
          <p className="eyebrow">Admin</p>
          <h1>Secure Log In</h1>
        </div>
        <form onSubmit={submit} className="sign-in-form admin-login-form">
          <label>
            <span>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) => update("email", event.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label>
            <span>Password</span>
            <input
              type="password"
              value={form.password}
              onChange={(event) => update("password", event.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <button type="submit" className="button primary" disabled={status.state === "loading"}>
            <img className="admin-login-logo" src={MANNA_LOGO_SRC} alt="" aria-hidden="true" />
            <span>{status.state === "loading" ? "Checking" : "Sign in"}</span>
          </button>
          <StatusLine status={status} />
        </form>
      </div>
    </section>
  );
}

function FlavorCardMedia({ flavor }) {
  const [imageFailed, setImageFailed] = useState(false);
  const hasImage = Boolean(flavor.imageUrl) && !imageFailed;

  useEffect(() => {
    setImageFailed(false);
  }, [flavor.imageUrl]);

  return (
    <span className={`admin-flavor-photo ${hasImage ? "has-image" : "is-empty"}`}>
      {hasImage ? (
        <img
          src={flavor.imageUrl}
          alt=""
          style={{ objectPosition: imagePositionValue(flavor.imagePosition) }}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <ImagePlus size={30} aria-hidden="true" />
      )}
    </span>
  );
}

function FlavorDetailPanel({ flavor, index, detailId, updateFlavor, removeFlavor, setStatus }) {
  return (
    <section className="admin-flavor-detail" id={detailId} aria-label={`Edit ${flavor.name || "flavor"}`}>
      <div className="admin-flavor-detail-head">
        <div>
          <p className="eyebrow">Edit flavor</p>
          <h3>{flavor.name || "Untitled flavor"}</h3>
        </div>
      </div>
      <div className="admin-flavor-detail-body admin-row admin-row-with-media">
        <AdminImageUpload
          imageUrl={flavor.imageUrl || ""}
          imagePosition={flavor.imagePosition || "center"}
          label={flavor.name || "gelato flavor"}
          kind="flavors"
          isOutOfSeason={flavor.isOutOfSeason === true}
          onUploaded={(url) => updateFlavor(index, "imageUrl", url)}
          setStatus={setStatus}
        />
        <div className="admin-row-fields">
          <label>
            <span>Name</span>
            <input value={flavor.name || ""} onChange={(event) => updateFlavor(index, "name", event.target.value)} />
          </label>
          <label>
            <span>Description</span>
            <input
              value={flavor.description || ""}
              onChange={(event) => updateFlavor(index, "description", event.target.value)}
            />
          </label>
          <label>
            <span>Tags</span>
            <input value={tagsText(flavor.tags)} onChange={(event) => updateFlavor(index, "tags", event.target.value)} />
          </label>
          <label>
            <span>Image focus</span>
            <select
              value={imagePositionValue(flavor.imagePosition)}
              onChange={(event) => updateFlavor(index, "imagePosition", event.target.value)}
            >
              {IMAGE_POSITION_OPTIONS.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="admin-check">
            <input
              type="checkbox"
              checked={flavor.isFeatured === true}
              onChange={(event) => updateFlavor(index, "isFeatured", event.target.checked)}
            />
            <span>Featured</span>
          </label>
          <label className="admin-check">
            <input
              type="checkbox"
              checked={flavor.isOutOfSeason !== true}
              onChange={(event) => updateFlavor(index, "isOutOfSeason", !event.target.checked)}
            />
            <span>In season</span>
          </label>
          <label className="admin-check">
            <input
              type="checkbox"
              checked={flavor.isActive !== false}
              onChange={(event) => updateFlavor(index, "isActive", event.target.checked)}
            />
            <span>Active</span>
          </label>
          <button type="button" className="icon-danger" onClick={() => removeFlavor(index)} aria-label="Remove flavor">
            <Trash2 size={18} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}

function FlavorEditor({ flavors, setFlavors, onSave, status, setStatus }) {
  const [openFlavorIndex, setOpenFlavorIndex] = useState(null);
  const inSeasonCount = useMemo(() => flavors.filter((flavor) => flavor.isOutOfSeason !== true).length, [flavors]);
  const outOfSeasonCount = flavors.length - inSeasonCount;

  useEffect(() => {
    if (openFlavorIndex !== null && openFlavorIndex >= flavors.length) {
      setOpenFlavorIndex(null);
    }
  }, [flavors.length, openFlavorIndex]);

  const updateFlavor = (index, field, value) => {
    setFlavors((current) =>
      current.map((flavor, flavorIndex) =>
        flavorIndex === index
          ? {
              ...flavor,
              [field]: value
            }
          : flavor
      )
    );
  };

  const removeFlavor = (index) => {
    setFlavors((current) => current.filter((_, flavorIndex) => flavorIndex !== index));
    setOpenFlavorIndex((current) => {
      if (current === index) {
        return null;
      }

      return current !== null && current > index ? current - 1 : current;
    });
  };

  const addFlavor = () => {
    const nextIndex = flavors.length;
    setFlavors((current) => [...current, { ...EMPTY_FLAVOR, id: rowId("flavor") }]);
    setOpenFlavorIndex(nextIndex);
  };

  return (
    <section className="admin-editor">
      <div className="admin-editor-head admin-flavor-head">
        <div>
          <p className="eyebrow">Gelato flavors</p>
          <h2>{flavors.length} flavors</h2>
          <p>{inSeasonCount} in season, {outOfSeasonCount} out of season</p>
        </div>
        <div className="admin-editor-head-actions">
          <button
            type="button"
            className="admin-icon-action admin-add-flavor-action"
            onClick={addFlavor}
            aria-label="Add flavor"
            title="Add flavor"
          >
            <Plus size={19} aria-hidden="true" />
          </button>
          <button type="button" className="button primary" onClick={onSave} disabled={status.state === "loading"}>
            <Save size={17} aria-hidden="true" />
            <span>Save flavors</span>
          </button>
        </div>
      </div>
      <div className="admin-flavor-list">
        {flavors.map((flavor, index) => {
          const flavorName = flavor.name || "Untitled flavor";
          const detailId = `admin-flavor-detail-${index}`;
          const isOpen = openFlavorIndex === index;
          const isInSeason = flavor.isOutOfSeason !== true;

          return (
            <Fragment key={flavor.id || `${flavor.name}-${index}`}>
              <article className={`admin-flavor-card${isOpen ? " is-open" : ""}${isInSeason ? "" : " is-out-of-season"}`}>
                <label className="admin-flavor-season-check" title={isInSeason ? "In season" : "Out of season"}>
                  <input
                    type="checkbox"
                    checked={isInSeason}
                    onChange={(event) => updateFlavor(index, "isOutOfSeason", !event.target.checked)}
                    aria-label={`${flavorName} is in season`}
                  />
                </label>
                <button
                  type="button"
                  className="admin-flavor-card-main"
                  onClick={() => setOpenFlavorIndex((current) => (current === index ? null : index))}
                  aria-expanded={isOpen}
                  aria-controls={detailId}
                >
                  <FlavorCardMedia flavor={flavor} />
                  <span className="admin-flavor-card-name">{flavorName}</span>
                  <span className="admin-flavor-card-status">{isInSeason ? "In season" : "Out of season"}</span>
                </button>
              </article>
              {isOpen ? (
                <FlavorDetailPanel
                  flavor={flavor}
                  index={index}
                  detailId={detailId}
                  updateFlavor={updateFlavor}
                  removeFlavor={removeFlavor}
                  setStatus={setStatus}
                />
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </section>
  );
}

function MenuEditor({ sections, setSections, onSave, status, setStatus }) {
  const [openItemIds, setOpenItemIds] = useState(() => new Set());
  const savedSectionsRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);
  const sectionsSignature = useMemo(() => JSON.stringify(sections), [sections]);
  const canonicalOptions = useMemo(
    () =>
      sections.flatMap((section, sectionIndex) =>
        (section.items || [])
          .filter((item) => isPersistedId(item.id))
          .map((item, itemIndex) => ({
            id: String(item.id),
            label: `${section.title || "Untitled section"} / ${item.name || "Untitled item"}`,
            sectionIndex,
            itemIndex
          }))
      ),
    [sections]
  );

  const updateSection = (index, field, value) => {
    updateSectionFields(index, { [field]: value });
  };

  const updateSectionFields = (index, patch) => {
    setSections((current) =>
      current.map((section, sectionIndex) => (sectionIndex === index ? { ...section, ...patch } : section))
    );
  };

  const updateItemFields = (sectionIndex, itemIndex, patch) => {
    setSections((current) =>
      current.map((section, currentSectionIndex) => {
        if (currentSectionIndex !== sectionIndex) {
          return section;
        }

        return {
          ...section,
          items: section.items.map((item, currentItemIndex) =>
            currentItemIndex === itemIndex ? { ...item, ...patch } : item
          )
        };
      })
    );
  };

  const updateItem = (sectionIndex, itemIndex, field, value) => {
    updateItemFields(sectionIndex, itemIndex, { [field]: value });
  };

  const addSection = () => {
    setSections((current) => [...current, { ...EMPTY_SECTION, id: rowId("section"), items: [] }]);
  };

  const removeSection = (index) => {
    const sectionName = sections[index]?.title || "this section";
    if (typeof window !== "undefined" && !window.confirm(`Remove ${sectionName} and all of its menu items? You can still cancel by not saving the menu.`)) {
      return;
    }
    setSections((current) => current.filter((_, sectionIndex) => sectionIndex !== index));
  };

  const addItem = (sectionIndex) => {
    const item = { ...EMPTY_ITEM, id: rowId("item") };
    setOpenItemIds((current) => new Set(current).add(item.id));
    setSections((current) =>
      current.map((section, index) =>
        index === sectionIndex
          ? { ...section, items: [...(section.items || []), item] }
          : section
      )
    );
  };

  const removeItem = (sectionIndex, itemIndex) => {
    const itemName = sections[sectionIndex]?.items?.[itemIndex]?.name || "this menu item";
    if (typeof window !== "undefined" && !window.confirm(`Remove ${itemName}? You can still cancel by not saving the menu.`)) {
      return;
    }
    setSections((current) =>
      current.map((section, index) =>
        index === sectionIndex
          ? { ...section, items: section.items.filter((_, currentItemIndex) => currentItemIndex !== itemIndex) }
          : section
      )
    );
  };

  useEffect(() => {
    if (savedSectionsRef.current === null) {
      savedSectionsRef.current = sectionsSignature;
      return;
    }
    setIsDirty(savedSectionsRef.current !== sectionsSignature);
  }, [sectionsSignature]);

  const saveMenu = async () => {
    const savedSections = await onSave();
    if (!savedSections) return;
    savedSectionsRef.current = JSON.stringify(savedSections);
    setIsDirty(false);
  };

  return (
    <section className="admin-editor">
      <div className="admin-editor-head">
        <div>
          <p className="eyebrow">Menu</p>
          <h2>{sections.length} sections</h2>
        </div>
        <div className="admin-actions">
          <button type="button" className="button outline" onClick={addSection}>
            <Plus size={17} aria-hidden="true" />
            <span>Add section</span>
          </button>
          <button type="button" className="button primary" onClick={saveMenu} disabled={status.state === "loading"}>
            <Save size={17} aria-hidden="true" />
            <span>Save menu</span>
          </button>
        </div>
      </div>
      <div className="admin-menu-editor">
        {sections.map((section, sectionIndex) => (
          <details open={sectionIndex < 2} className="admin-section-card" key={section.id || `${section.title}-${sectionIndex}`}>
            <summary>
              <span>
                {section.title || "Untitled section"}
                {section.isStatusActive && section.statusLabel ? (
                  <em className="admin-section-status-pill">{section.statusLabel}</em>
                ) : null}
              </span>
              <strong>{section.items?.length || 0} items</strong>
            </summary>
            <div className="admin-section-fields">
              <label>
                <span>Section title</span>
                <input value={section.title || ""} onChange={(event) => updateSection(sectionIndex, "title", event.target.value)} />
              </label>
              <label>
                <span>Note</span>
                <input value={section.note || ""} onChange={(event) => updateSection(sectionIndex, "note", event.target.value)} />
              </label>
              <label className="admin-check">
                <input
                  type="checkbox"
                  checked={section.isActive !== false}
                  onChange={(event) => updateSection(sectionIndex, "isActive", event.target.checked)}
                />
                <span>Active</span>
              </label>
              {isCoffeeSection(section) || section.statusLabel ? (
                <label className="admin-check admin-status-check">
                  <input
                    type="checkbox"
                    checked={section.isStatusActive === true}
                    onChange={(event) =>
                      updateSectionFields(sectionIndex, {
                        statusLabel: section.statusLabel || "Coffee Machine Temporarily Down",
                        isStatusActive: event.target.checked
                      })
                    }
                  />
                  <span>{section.statusLabel || "Coffee Machine Temporarily Down"}</span>
                </label>
              ) : null}
              <button type="button" className="icon-danger" onClick={() => removeSection(sectionIndex)} aria-label="Remove section">
                <Trash2 size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="admin-items">
              {(section.items || []).map((item, itemIndex) => {
                const itemKey = String(item.id || `${sectionIndex}-${itemIndex}`);
                const itemStatus = item.isActive === false ? "Hidden" : item.isOutOfSeason ? "Out of season" : "Active";

                return (
                <article className="admin-item-card" key={itemKey}>
                  <details
                    className="admin-item-details"
                    open={openItemIds.has(itemKey)}
                    onToggle={(event) => {
                      const isOpen = event.currentTarget.open;
                      setOpenItemIds((current) => {
                        const next = new Set(current);
                        if (isOpen) next.add(itemKey);
                        else next.delete(itemKey);
                        return next;
                      });
                    }}
                  >
                    <summary>
                      <span className="admin-item-summary-image" aria-hidden="true">
                        {item.imageUrl ? <img src={item.imageUrl} alt="" /> : <ImagePlus size={19} />}
                      </span>
                      <span className="admin-item-summary-copy">
                        <strong>{item.name || "Untitled item"}</strong>
                        <span>{item.price || "No price set"}</span>
                      </span>
                      <span className={`admin-item-summary-status${item.isActive === false ? " is-hidden" : ""}`}>{itemStatus}</span>
                    </summary>
                    <div className="admin-item-row admin-item-row-with-media">
                  <AdminImageUpload
                    imageUrl={item.imageUrl || ""}
                    imageAlt={item.imageAlt || item.name || ""}
                    imagePosition={item.imagePosition || "center"}
                    label={item.name || "menu item"}
                    kind="menu-items"
                    isOutOfSeason={item.isOutOfSeason === true}
                    onUploaded={(url) =>
                      updateItemFields(sectionIndex, itemIndex, {
                        imageUrl: url,
                        imageAlt: item.imageAlt || item.name || ""
                      })
                    }
                    setStatus={setStatus}
                  />
                  <div className="admin-item-fields">
                    <label>
                      <span>Name</span>
                      <input
                        value={item.name || ""}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "name", event.target.value)}
                      />
                    </label>
                    <label>
                      <span>Price</span>
                      <input
                        value={item.price || ""}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "price", event.target.value)}
                      />
                    </label>
                    <label className="wide">
                      <span>Linked menu item</span>
                      <select
                        value={item.canonicalItemId || ""}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "canonicalItemId", event.target.value)}
                      >
                        <option value="">None, standalone item</option>
                        {canonicalOptions
                          .filter(
                            (option) =>
                              option.id !== String(item.id || "") &&
                              !(option.sectionIndex === sectionIndex && option.itemIndex === itemIndex)
                          )
                          .map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                      </select>
                      {item.canonicalItemId ? (
                        <small>
                          Public menus inherit content from {item.canonicalLabel || "the linked item"}. This row keeps its own section, price, position, and active state.
                        </small>
                      ) : null}
                    </label>
                    <label className="wide">
                      <span>{item.canonicalItemId ? "Local note" : "Description"}</span>
                      <input
                        value={item.description || ""}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "description", event.target.value)}
                      />
                    </label>
                    <label>
                      <span>Image alt</span>
                      <input
                        value={item.imageAlt || ""}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "imageAlt", event.target.value)}
                      />
                    </label>
                    <label>
                      <span>Image focus</span>
                      <select
                        value={imagePositionValue(item.imagePosition)}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "imagePosition", event.target.value)}
                      >
                        {IMAGE_POSITION_OPTIONS.map(([value, label]) => (
                          <option value={value} key={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Tags</span>
                      <input
                        value={tagsText(item.tags)}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "tags", event.target.value)}
                      />
                    </label>
                    <label className="admin-check">
                      <input
                        type="checkbox"
                        checked={item.isFeatured === true}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "isFeatured", event.target.checked)}
                      />
                      <span>Featured</span>
                    </label>
                    <label className="admin-check">
                      <input
                        type="checkbox"
                        checked={item.isSecret === true}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "isSecret", event.target.checked)}
                      />
                      <span>Search only</span>
                    </label>
                    <label className="admin-check">
                      <input
                        type="checkbox"
                        checked={item.isOutOfSeason === true}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "isOutOfSeason", event.target.checked)}
                      />
                      <span>Out of season</span>
                    </label>
                    <label className="admin-check">
                      <input
                        type="checkbox"
                        checked={item.isActive !== false}
                        onChange={(event) => updateItem(sectionIndex, itemIndex, "isActive", event.target.checked)}
                      />
                      <span>Active</span>
                    </label>
                    <button type="button" className="icon-danger" onClick={() => removeItem(sectionIndex, itemIndex)} aria-label="Remove item">
                      <Trash2 size={18} aria-hidden="true" />
                    </button>
                  </div>
                    </div>
                  </details>
                </article>
                );
              })}
            </div>
            <button type="button" className="button outline admin-add-item" onClick={() => addItem(sectionIndex)}>
              <Plus size={17} aria-hidden="true" />
              <span>Add item</span>
            </button>
          </details>
        ))}
      </div>
      {typeof document !== "undefined"
        ? createPortal(
            <div className="admin-menu-save-bar" aria-live="polite">
              <span>{isDirty ? "Unsaved changes" : "All changes saved"}</span>
              <button type="button" className="button primary" onClick={saveMenu} disabled={!isDirty || status.state === "loading"}>
                <Save size={17} aria-hidden="true" />
                <span>{status.state === "loading" ? "Saving..." : "Save changes"}</span>
              </button>
            </div>,
            document.body
          )
        : null}
    </section>
  );
}

function FormsEditor({ forms, setForms, submissions, email, onSave, status }) {
  const updateForm = (index, field, value) => {
    setForms((current) =>
      current.map((form, formIndex) =>
        formIndex === index
          ? {
              ...form,
              [field]: value
            }
          : form
      )
    );
  };

  const addForm = () => {
    setForms((current) => [...current, { ...EMPTY_FORM, id: rowId("catering_form") }]);
  };

  const removeForm = (index) => {
    setForms((current) => current.filter((_, formIndex) => formIndex !== index));
  };

  return (
    <section className="admin-editor">
      <div className="admin-editor-head">
        <div>
          <p className="eyebrow">Forms</p>
          <h2>Catering requests and email routing</h2>
        </div>
        <div className="admin-actions">
          <button type="button" className="button outline" onClick={addForm}>
            <Plus size={17} aria-hidden="true" />
            <span>Add form</span>
          </button>
          <button type="button" className="button primary" onClick={onSave} disabled={status.state === "loading"}>
            <Save size={17} aria-hidden="true" />
            <span>Save forms</span>
          </button>
        </div>
      </div>

      <div className={`email-provider-card ${email.configured ? "configured" : ""}`}>
        <Mail size={20} aria-hidden="true" />
        <div>
          <strong>{email.configured ? "Resend is configured" : "Resend is not configured yet"}</strong>
          <p>
            {email.configured
              ? `Sending from ${email.from}.`
              : "Submissions will save to the database now. Add RESEND_API_KEY and CATERING_EMAIL_FROM in Railway to send real email."}
          </p>
        </div>
      </div>

      <div className="admin-list">
        {forms.map((form, index) => (
          <article className="admin-form-row" key={form.id || index}>
            <label>
              <span>Form ID</span>
              <input value={form.id || ""} onChange={(event) => updateForm(index, "id", event.target.value)} />
            </label>
            <label>
              <span>Name</span>
              <input value={form.name || ""} onChange={(event) => updateForm(index, "name", event.target.value)} />
            </label>
            <label className="wide">
              <span>Recipient emails</span>
              <input
                value={emailsText(form.recipientEmails)}
                onChange={(event) => updateForm(index, "recipientEmails", event.target.value)}
                placeholder="hello@gelato.spot, catering@example.com"
              />
            </label>
            <label className="wide">
              <span>Subject template</span>
              <input
                value={form.subjectTemplate || ""}
                onChange={(event) => updateForm(index, "subjectTemplate", event.target.value)}
                placeholder="New catering request from {name}"
              />
            </label>
            <label className="wide">
              <span>Description</span>
              <textarea
                rows="3"
                value={form.description || ""}
                onChange={(event) => updateForm(index, "description", event.target.value)}
              />
            </label>
            <label className="admin-check">
              <input
                type="checkbox"
                checked={form.isActive !== false}
                onChange={(event) => updateForm(index, "isActive", event.target.checked)}
              />
              <span>Active</span>
            </label>
            <button type="button" className="icon-danger" onClick={() => removeForm(index)} aria-label="Remove form">
              <Trash2 size={18} aria-hidden="true" />
            </button>
          </article>
        ))}
      </div>

      <div className="admin-submissions">
        <div className="admin-submissions-head">
          <div>
            <p className="eyebrow">Submissions</p>
            <h3>{submissions.length} recent requests</h3>
          </div>
          <Inbox size={24} aria-hidden="true" />
        </div>
        {submissions.length ? (
          <div className="submission-list">
            {submissions.map((submission) => (
              <article className="submission-card" key={submission.id}>
                <div>
                  <strong>{submission.name}</strong>
                  <span>{formatDateTime(submission.createdAt)}</span>
                </div>
                <p>
                  {submission.cateringType || "Catering"} for {submission.guestCount || "unknown"} guests
                  {submission.phone ? `, ${submission.phone}` : ""}.
                </p>
                <p>{submission.email}</p>
                {submission.eventNotes ? <p>{submission.eventNotes}</p> : null}
                <div className="submission-meta">
                  <span>Status: {submission.status}</span>
                  <span>Email: {submission.emailStatus}</span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="form-status">No catering form submissions yet.</p>
        )}
      </div>
    </section>
  );
}

function CateringEditor({ algorithm, setAlgorithm, onSave, status }) {
  const update = (field, value) => {
    setAlgorithm((current) => ({
      ...current,
      [field]: value
    }));
  };

  return (
    <section className="admin-editor">
      <div className="admin-editor-head">
        <div>
          <p className="eyebrow">Catering algorithm</p>
          <h2>Estimator pricing</h2>
        </div>
        <button type="button" className="button primary" onClick={onSave} disabled={status.state === "loading"}>
          <Save size={17} aria-hidden="true" />
          <span>Save algorithm</span>
        </button>
      </div>
      <div className="admin-catering-grid">
        {CATERING_FIELDS.map(([field, label]) => (
          <label key={field}>
            <span>{label}</span>
            <input
              type="number"
              step={field.toLowerCase().includes("price") || field === "staffFee" || field === "basePrice" ? "0.01" : "1"}
              value={algorithm[field] ?? ""}
              onChange={(event) => update(field, event.target.value)}
            />
          </label>
        ))}
        <label className="wide">
          <span>Calculator description</span>
          <textarea
            value={algorithm.description || ""}
            rows="3"
            onChange={(event) => update("description", event.target.value)}
          />
        </label>
      </div>
    </section>
  );
}

function formatRankingCurrency(value) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(Number(value || 0));
}

function formatRankingQuantity(value) {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2
  }).format(Number(value || 0));
}

function RankingQuantityBar({ row, variants, barWidth, interactive }) {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const selected = variants.find((variant) => variant.label === selectedVariant);

  return (
    <>
      <span className={`ranking-bar${interactive ? " interactive" : ""}`} aria-label={`Quantity popularity bar for ${row.name}`}>
        <span className="ranking-bar-fill" style={{ width: `${barWidth}%` }}>
          {variants.map((variant) => {
            const color = RANKING_VARIANT_COLORS[variant.label] || RANKING_COLORS[0];

            return interactive ? (
              <button
                key={variant.label}
                type="button"
                className={`ranking-bar-segment${selectedVariant === variant.label ? " active" : ""}`}
                style={{ width: `${variant.share}%` }}
                aria-label={`${variant.label}: ${formatRankingQuantity(variant.units)} sold, ${formatRankingCurrency(variant.sales)} revenue`}
                aria-pressed={selectedVariant === variant.label}
                onClick={() => setSelectedVariant((current) => current === variant.label ? null : variant.label)}
              >
                <i style={{ backgroundColor: color }} />
              </button>
            ) : (
              <i
                key={variant.label}
                style={{ width: `${variant.share}%`, backgroundColor: color }}
              />
            );
          })}
        </span>
      </span>
      {selected ? (
        <span className="ranking-variant-breakdown" role="status" aria-live="polite">
          <strong>{selected.label}</strong>
          <span>{formatRankingQuantity(selected.units)} sold</span>
          <span>{formatRankingCurrency(selected.sales)} revenue</span>
        </span>
      ) : null}
    </>
  );
}

function RankingEditor() {
  const [category, setCategory] = useState("gelato");
  const [period, setPeriod] = useState("this_month");
  const [view, setView] = useState("list");
  const [ranking, setRanking] = useState(null);
  const [rankingStatus, setRankingStatus] = useState({ state: "loading", message: "Loading rankings..." });

  useEffect(() => {
    const controller = new AbortController();
    setRankingStatus({ state: "loading", message: "Loading rankings..." });

    requestJson(`/api/admin/rankings?category=${encodeURIComponent(category)}&period=${encodeURIComponent(period)}`, {
      signal: controller.signal
    })
      .then((payload) => {
        if (!payload?.summary || !Array.isArray(payload.rows)) {
          throw new Error("Ranking data is unavailable. Refresh the admin after the local server restarts.");
        }
        setRanking(payload);
        setRankingStatus({ state: "idle", message: "" });
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setRanking(null);
        setRankingStatus({ state: "error", message: error.message });
      });

    return () => controller.abort();
  }, [category, period]);

  const pieBackground = useMemo(() => {
    const rows = ranking?.rows || [];
    if (!rows.length) return "conic-gradient(#e7e3dc 0 100%)";
    let start = 0;
    const slices = rows.slice(0, 8).map((row, index) => {
      const end = start + row.share;
      const slice = `${RANKING_COLORS[index]} ${start}% ${end}%`;
      start = end;
      return slice;
    });
    if (start < 100) slices.push(`#e7e3dc ${start}% 100%`);
    return `conic-gradient(${slices.join(", ")})`;
  }, [ranking]);

  const isGelato = category === "gelato";
  const rows = ranking?.rows || [];
  const maxUnits = Math.max(1, ...rows.map((row) => Number(row.units || 0)));
  const hasDiningVariants = category === "dining" && rows.some((row) =>
    row.variants?.some((variant) => variant.label === "Dinner" || variant.label === "Lunch")
  );

  return (
    <section className="admin-editor ranking-editor">
      <div className="admin-editor-head ranking-head">
        <div>
          <p className="eyebrow">Ranking</p>
          <h2>{RANKING_CATEGORIES.find((entry) => entry.id === category)?.label} performance</h2>
          <p>{ranking?.range?.label || "Choose a period to compare demand."}</p>
        </div>
        <div className="ranking-view-toggle" aria-label="Ranking presentation">
          <button type="button" className={view === "list" ? "active" : ""} onClick={() => setView("list")} aria-pressed={view === "list"}>
            <BarChart3 size={17} aria-hidden="true" />
            <span>List</span>
          </button>
          <button type="button" className={view === "pie" ? "active" : ""} onClick={() => setView("pie")} aria-pressed={view === "pie"}>
            <ChartPie size={17} aria-hidden="true" />
            <span>Pie</span>
          </button>
        </div>
      </div>

      <div className="ranking-controls" aria-label="Ranking filters">
        <div className="ranking-category-tabs" aria-label="Ranking category">
          {RANKING_CATEGORIES.map(({ id, label, Icon }) => (
            <button key={id} type="button" aria-pressed={category === id} className={category === id ? "active" : ""} onClick={() => setCategory(id)}>
              <Icon size={17} aria-hidden="true" />
              <span>{label}</span>
            </button>
          ))}
        </div>
        <label className="ranking-period-select">
          <span>Period</span>
          <select value={period} onChange={(event) => setPeriod(event.target.value)}>
            {RANKING_PERIODS.map(([id, label, detail]) => (
              <option value={id} key={id}>{detail ? `${label} (${detail})` : label}</option>
            ))}
          </select>
        </label>
      </div>

      {rankingStatus.state === "loading" ? <p className="form-status loading">Loading ranking data...</p> : null}
      {rankingStatus.state === "error" ? <p className="form-status error">{rankingStatus.message}</p> : null}

      {!rankingStatus.message && ranking?.summary ? (
        <>
          {view === "list" && hasDiningVariants ? (
            <div className="ranking-variant-key" aria-label="Dining size colors">
              {Object.entries(RANKING_VARIANT_COLORS).map(([label, color]) => (
                <span key={label}><i style={{ backgroundColor: color }} />{label}</span>
              ))}
            </div>
          ) : null}
          {rows.length ? view === "list" ? (
            <ol className="ranking-list">
              {rows.map((row) => {
                const variants = category === "dining" && row.variants?.length
                  ? row.variants
                  : [{ label: "Dinner", share: 100 }];
                const barWidth = Math.max(2, (Number(row.units || 0) / maxUnits) * 100);

                return (
                <li key={row.name}>
                  <span className="ranking-position" aria-label={`${formatRankingQuantity(row.units)} sold`}>
                    {formatRankingQuantity(row.units)}
                  </span>
                  <div className="ranking-item-copy">
                    <strong>{row.name}</strong>
                    <RankingQuantityBar
                      key={`${period}:${row.name}`}
                      row={row}
                      variants={variants}
                      barWidth={barWidth}
                      interactive={category === "dining"}
                    />
                  </div>
                  <div className="ranking-value" aria-label={`Revenue ${formatRankingCurrency(row.sales)}`}>
                    <strong>{formatRankingCurrency(row.sales)}</strong>
                  </div>
                </li>
                );
              })}
            </ol>
          ) : (
            <div className="ranking-pie-layout">
              <div className="ranking-pie" style={{ background: pieBackground }} aria-hidden="true"><span>Popularity</span></div>
              <ol className="ranking-pie-legend">
                {rows.slice(0, 8).map((row, index) => (
                  <li key={row.name}>
                    <i style={{ backgroundColor: RANKING_COLORS[index] }} />
                    <span>{row.name}</span>
                    <strong>{formatRankingCurrency(row.sales)}</strong>
                  </li>
                ))}
              </ol>
            </div>
          ) : <p className="form-status">No trustworthy {isGelato ? "flavor" : category} data is available for this period yet.</p>}
        </>
      ) : null}
    </section>
  );
}

function AdminTabs({ activeTab, className = "", onTabChange }) {
  return (
    <nav className={["admin-tabs", className].filter(Boolean).join(" ")} aria-label="Admin sections">
      {ADMIN_TABS.map(({ id: tab, label, Icon }) => (
        <button
          type="button"
          key={tab}
          className={activeTab === tab ? "active" : ""}
          onClick={() => onTabChange(tab)}
        >
          <Icon size={18} aria-hidden="true" />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

function SubscribersEditor({ data, sms, marketingEmail, onCampaignSent }) {
  const subscribers = data?.subscribers || [];
  const [message, setMessage] = useState("");
  const [sendStatus, setSendStatus] = useState({ state: "idle", message: "" });

  const sendCampaign = async () => {
    const recipientCount = data?.smsCount || 0;
    if (!window.confirm(`Send this text to ${recipientCount} opted-in subscriber${recipientCount === 1 ? "" : "s"}?`)) {
      return;
    }

    setSendStatus({ state: "loading", message: "Queueing texts through Twilio..." });
    try {
      const payload = await requestJson("/api/admin/sms-campaigns", {
        method: "POST",
        body: JSON.stringify({ body: message })
      });
      const campaign = payload.campaign || {};
      setMessage("");
      setSendStatus({
        state: campaign.failed ? "error" : "success",
        message: `Queued ${campaign.sent || 0} of ${campaign.recipients || 0} texts${campaign.failed ? `; ${campaign.failed} failed` : ""}.`
      });
      onCampaignSent?.();
    } catch (error) {
      setSendStatus({ state: "error", message: error.message });
    }
  };

  return (
    <section className="admin-editor">
      <div className="admin-editor-head">
        <div>
          <p className="eyebrow">Permission-based list</p>
          <h2>{data?.count || 0} active subscribers</h2>
          <p>These contacts joined through the Gelato Spot consent form. The historical Shift4 export is not included.</p>
        </div>
        <div className="admin-editor-head-actions">
          <a className="button primary" href="/api/admin/subscribers.csv">
            <Mail size={17} aria-hidden="true" />
            <span>Export consented CSV</span>
          </a>
        </div>
      </div>
      <div className={`email-campaign-card${marketingEmail?.sendingEnabled ? " is-ready" : ""}`}>
        <div className="sms-campaign-head">
          <div>
            <p className="eyebrow">Resend email broadcasts</p>
            <h3>{data?.emailCount || 0} email subscribers</h3>
          </div>
          <span className="sms-status-pill">
            {marketingEmail?.sendingEnabled
              ? "Ready to send"
              : marketingEmail?.syncEnabled
                ? "Audience syncing"
                : marketingEmail?.syncConfigured
                  ? "Sync paused"
                  : "Setup required"}
          </span>
        </div>
        <p>
          New website opt-ins sync to the Resend segment <strong>Gelato Spot subscribers</strong> and topic <strong>Gelato Spot updates</strong>.
        </p>
        <div className="sms-campaign-actions">
          <p>
            Create the email in Resend, target this segment and topic, and keep Resend's unsubscribe footer enabled.
          </p>
          <a
            className="button primary"
            href={marketingEmail?.broadcastsUrl || "https://resend.com/broadcasts"}
            target="_blank"
            rel="noreferrer"
          >
            <Mail size={17} aria-hidden="true" />
            <span>Open Resend broadcasts</span>
          </a>
        </div>
        {!marketingEmail?.sendingEnabled ? (
          <p className="sms-setup-note">
            {marketingEmail?.syncEnabled
              ? "Audience syncing is live. Email sending stays paused until gelato.spot is verified in Resend."
              : "Finish the Resend audience settings before syncing or sending email."}
          </p>
        ) : null}
      </div>
      <div className={`sms-campaign-card${sms?.enabled ? " is-ready" : ""}`}>
        <div className="sms-campaign-head">
          <div>
            <p className="eyebrow">Twilio text campaign</p>
            <h3>{data?.smsCount || 0} text subscribers</h3>
          </div>
          <span className="sms-status-pill">
            {sms?.enabled ? "Ready to send" : sms?.configured ? "Sending paused" : "Setup required"}
          </span>
        </div>
        <label>
          <span>Message</span>
          <textarea
            value={message}
            maxLength="420"
            rows="4"
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Tonight only: two scoops for the price of one."
          />
        </label>
        <div className="sms-campaign-actions">
          <p>
            The server adds “The Gelato Spot:” and “Reply STOP to unsubscribe.” when they are missing. {message.length}/420
          </p>
          <button
            type="button"
            className="button primary"
            onClick={sendCampaign}
            disabled={!sms?.enabled || !message.trim() || !(data?.smsCount > 0) || sendStatus.state === "loading"}
          >
            <Send size={17} aria-hidden="true" />
            <span>{sendStatus.state === "loading" ? "Queueing..." : "Send opted-in texts"}</span>
          </button>
        </div>
        {!sms?.enabled ? (
          <p className="sms-setup-note">
            {sms?.configured
              ? "Twilio is configured, but the outbound safety switch remains off pending campaign approval."
              : "Add the Gelato Spot Twilio credentials and approved Messaging Service in Railway before sending."}
          </p>
        ) : null}
        <StatusLine status={sendStatus} />
      </div>
      {subscribers.length ? (
        <div className="submission-list">
          {subscribers.map((subscriber) => (
            <article className="submission-card" key={subscriber.id}>
              <div>
                <strong>{subscriber.name}</strong>
                <span>{formatDateTime(subscriber.createdAt)}</span>
              </div>
              <p>{subscriber.email}</p>
              <div className="submission-meta">
                <span>Email: {subscriber.emailConsent ? "opted in" : "unsubscribed"}</span>
                <span>Resend: {subscriber.resendSyncStatus || "not synced"}</span>
                <span>Text: {subscriber.smsConsent ? "opted in" : "not opted in"}</span>
                <span>Source: {subscriber.source || "website"}</span>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <p className="form-status">No consented subscribers yet. Share gelato.spot/join to start the list.</p>
      )}
    </section>
  );
}

export default function AdminPage() {
  const [authState, setAuthState] = useState("loading");
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [activeTab, setActiveTab] = useState("flavors");
  const [user, setUser] = useState(null);
  const [sections, setSections] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [algorithm, setAlgorithm] = useState(DEFAULT_CATERING_ALGORITHM);
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [subscribers, setSubscribers] = useState({ count: 0, subscribers: [] });
  const [sms, setSms] = useState({ provider: "twilio", configured: false, enabled: false, missing: [] });
  const [marketingEmail, setMarketingEmail] = useState({
    provider: "resend",
    syncConfigured: false,
    syncEnabled: false,
    sendingEnabled: false,
    broadcastsUrl: "https://resend.com/broadcasts"
  });
  const [email, setEmail] = useState({ provider: "resend", configured: false, from: "" });
  const [storage, setStorage] = useState({ configured: false, uploadMode: "unavailable", missing: [] });

  const loadData = async () => {
    setAuthState("loading");
    setStatus({ state: "loading", message: "Loading admin..." });

    try {
      const payload = await requestJson("/api/admin/data");
      const normalized = normalizeAdminData(payload);
      const me = await requestJson("/api/admin/me");

      setSections(normalized.menu.sections);
      setFlavors(normalized.flavors);
      setAlgorithm(normalized.catering);
      setForms(normalized.forms);
      setSubmissions(normalized.submissions);
      setSubscribers(normalized.subscribers);
      setSms(normalized.sms);
      setMarketingEmail(normalized.marketingEmail);
      setEmail(normalized.email);
      setStorage(normalized.storage);
      setUser(me.user);
      setAuthState("ready");
      setStatus({ state: "idle", message: "" });
    } catch (error) {
      if (error.status === 401) {
        setAuthState("signed-out");
        setStatus({ state: "idle", message: "" });
        return;
      }

      setAuthState("blocked");
      setStatus({ state: "error", message: error.message });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const logout = useCallback(async () => {
    await requestJson("/api/auth/logout", { method: "POST" }).catch(() => ({}));
    setUser(null);
    setAuthState("signed-out");
    setStatus({ state: "idle", message: "" });
  }, []);

  useEffect(() => {
    const handleHeaderLogout = () => {
      logout();
    };

    window.addEventListener(ADMIN_LOGOUT_EVENT, handleHeaderLogout);
    return () => window.removeEventListener(ADMIN_LOGOUT_EVENT, handleHeaderLogout);
  }, [logout]);

  const saveFlavors = async () => {
    setStatus({ state: "loading", message: "Saving flavors..." });
    try {
      const payload = await requestJson("/api/admin/flavors", {
        method: "PUT",
        body: JSON.stringify({ flavors })
      });
      setFlavors(payload.flavors.flavors || flavors);
      setStatus({ state: "success", message: "Flavors saved." });
    } catch (error) {
      setStatus({ state: "error", message: error.message });
    }
  };

  const saveMenu = async () => {
    setStatus({ state: "loading", message: "Saving menu..." });
    try {
      const payload = await requestJson("/api/admin/menu", {
        method: "PUT",
        body: JSON.stringify({ sections })
      });
      const savedSections = payload.menu.sections || sections;
      setSections(savedSections);
      setStatus({ state: "success", message: "Menu saved." });
      return savedSections;
    } catch (error) {
      setStatus({ state: "error", message: error.message });
      return null;
    }
  };

  const saveAlgorithm = async () => {
    setStatus({ state: "loading", message: "Saving catering algorithm..." });
    try {
      const payload = await requestJson("/api/admin/catering-algorithm", {
        method: "PUT",
        body: JSON.stringify({ algorithm })
      });
      setAlgorithm(normalizeCateringAlgorithm(payload.catering?.algorithm || algorithm));
      setStatus({ state: "success", message: "Catering algorithm saved." });
    } catch (error) {
      setStatus({ state: "error", message: error.message });
    }
  };

  const saveForms = async () => {
    setStatus({ state: "loading", message: "Saving catering forms..." });
    try {
      const payload = await requestJson("/api/admin/catering-forms", {
        method: "PUT",
        body: JSON.stringify({ forms })
      });
      setForms(payload.forms.forms || forms);
      setStatus({ state: "success", message: "Catering forms saved." });
    } catch (error) {
      setStatus({ state: "error", message: error.message });
    }
  };

  if (authState === "signed-out") {
    return <AdminLogin onLogin={loadData} status={status} setStatus={setStatus} />;
  }

  if (authState === "blocked") {
    return (
      <section className="admin-page admin-gate">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Access needed</h1>
          <p>{status.message}</p>
          <div className="button-row">
            <button type="button" className="button primary" onClick={loadData}>
              <RefreshCw size={17} aria-hidden="true" />
              <span>Try again</span>
            </button>
            <button type="button" className="button outline" onClick={logout}>
              <LogOut size={17} aria-hidden="true" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <header className="admin-header">
        <div>
          <p className="eyebrow">Gelato Spot Admin</p>
        </div>
        <div className="admin-actions admin-actions-desktop">
          <button type="button" className="button outline" onClick={loadData} disabled={status.state === "loading"}>
            <RefreshCw size={17} aria-hidden="true" />
            <span>Refresh</span>
          </button>
          <button type="button" className="button outline" onClick={logout}>
            <LogOut size={17} aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </div>
        <div className="admin-mobile-actions">
          <button
            type="button"
            className={`admin-icon-action${status.state === "loading" ? " is-loading" : ""}`}
            onClick={loadData}
            disabled={status.state === "loading"}
            aria-label="Refresh admin data"
            title="Refresh admin data"
          >
            <RefreshCw size={19} aria-hidden="true" />
          </button>
        </div>
      </header>

      <AdminTabs activeTab={activeTab} className="admin-tabs-inline" onTabChange={setActiveTab} />
      {typeof document !== "undefined"
        ? createPortal(
            <AdminTabs activeTab={activeTab} className="admin-tabs-mobile" onTabChange={setActiveTab} />,
            document.body
          )
        : null}

      <StorageNotice storage={storage} />
      <StatusLine status={status} />

      {activeTab === "flavors" ? (
        <FlavorEditor flavors={flavors} setFlavors={setFlavors} onSave={saveFlavors} status={status} setStatus={setStatus} />
      ) : null}
      {activeTab === "menu" ? (
        <MenuEditor sections={sections} setSections={setSections} onSave={saveMenu} status={status} setStatus={setStatus} />
      ) : null}
      {activeTab === "ranking" ? <RankingEditor /> : null}
      {activeTab === "subscribers" ? (
        <SubscribersEditor
          data={subscribers}
          sms={sms}
          marketingEmail={marketingEmail}
          onCampaignSent={loadData}
        />
      ) : null}
      {activeTab === "forms" ? (
        <FormsEditor
          forms={forms}
          setForms={setForms}
          submissions={submissions}
          email={email}
          onSave={saveForms}
          status={status}
        />
      ) : null}
      {activeTab === "catering" ? (
        <CateringEditor algorithm={algorithm} setAlgorithm={setAlgorithm} onSave={saveAlgorithm} status={status} />
      ) : null}

      {user?.email ? <footer className="admin-session">Signed in as {user.email}</footer> : null}
    </section>
  );
}
